import React, { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Users, BookOpen, Calendar, Edit, X, Check, Plus } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Journalist } from '@/hooks/journalist/types';

interface ProfileMetricsProps {
  metrics: {
    followers: number;
    articles: number;
    yearsActive: number;
  };
  credentials: string[];
  isOwnProfile: boolean;
  updateProfile: (updates: Partial<Journalist>) => Promise<void>;
}

const ProfileMetrics: React.FC<ProfileMetricsProps> = ({ 
  metrics, 
  credentials, 
  isOwnProfile,
  updateProfile
}) => {
  const [editMode, setEditMode] = useState(false);
  const [editCredentials, setEditCredentials] = useState<string[]>(credentials || []);
  const [newCredential, setNewCredential] = useState('');
  
  const addCredential = () => {
    if (newCredential.trim() && !editCredentials.includes(newCredential.trim())) {
      setEditCredentials([...editCredentials, newCredential.trim()]);
      setNewCredential('');
    }
  };
  
  const removeCredential = (credentialToRemove: string) => {
    setEditCredentials(editCredentials.filter(cred => cred !== credentialToRemove));
  };
  
  const saveChanges = async () => {
    await updateProfile({
      credentials: editCredentials
    });
    setEditMode(false);
  };
  
  const cancelChanges = () => {
    setEditCredentials(credentials || []);
    setEditMode(false);
  };
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <Card className="col-span-1">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Metrics</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Users className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="text-2xl font-bold">{metrics.followers.toLocaleString()}</div>
              <div className="text-sm text-muted-foreground">Followers</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <BookOpen className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="text-2xl font-bold">{metrics.articles}</div>
              <div className="text-sm text-muted-foreground">Articles</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Calendar className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="text-2xl font-bold">{metrics.yearsActive}</div>
              <div className="text-sm text-muted-foreground">Years</div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="col-span-1 md:col-span-2">
        <CardContent className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Credentials</h3>
            {isOwnProfile && (
              editMode ? (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={cancelChanges}>
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={saveChanges}>
                    <Check className="h-4 w-4 mr-1" />
                    Save
                  </Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )
            )}
          </div>
          
          {editMode ? (
            <div className="space-y-3">
              {editCredentials.map((credential, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="h-2 w-2 mt-2 rounded-full bg-primary flex-shrink-0"></div>
                  <Input 
                    value={credential}
                    onChange={(e) => {
                      const updated = [...editCredentials];
                      updated[index] = e.target.value;
                      setEditCredentials(updated);
                    }}
                    className="flex-grow"
                  />
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => removeCredential(credential)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="flex items-center gap-2 mt-4">
                <Input 
                  value={newCredential}
                  onChange={(e) => setNewCredential(e.target.value)}
                  placeholder="Add new credential..."
                  className="flex-grow"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCredential())}
                />
                <Button 
                  variant="outline" 
                  onClick={addCredential}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {credentials.length > 0 ? (
                credentials.map((credential, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="h-2 w-2 mt-2 rounded-full bg-primary"></div>
                    <span>{credential}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground">No credentials added yet.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileMetrics;
